import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import type { UpsertPromptRequest } from "@/api/models"

import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { SettingsService } from "@/services/settings-service"

type PromptLibrariesSettingsProps = {
  updateData: Partial<UpsertPromptRequest>
  onUpdate: (data: Partial<UpsertPromptRequest>) => void
  sessionType: string
}

export function PromptLibrariesSettings({
  updateData,
  onUpdate,
  sessionType,
}: PromptLibrariesSettingsProps) {
  const { t } = useTranslation("", { keyPrefix: "prompt_settings.libraries" })

  // Get libraries directly from SettingsService, filtered by sessionType
  const availableLibraries = useMemo(() => {
    const settingsService = SettingsService.getInstance()
    return settingsService.settings.indexes
      .filter((index) => {
        // If blocked_modules is missing or empty, include for all sessionTypes
        if (!index.blocked_modules || index.blocked_modules.length === 0) {
          return true
        }
        // Otherwise, check if sessionType is NOT in blocked_modules array
        return !index.blocked_modules.includes(sessionType)
      })
      .map((index) => ({
        id: index.id,
        display_name: index.display_name,
        description: index.description,
      }))
  }, [sessionType])

  const enabledIndexIds = updateData.enabledIndexIds || []
  const isAllLibrariesEnabled = availableLibraries.every((lib) => enabledIndexIds.includes(lib.id))

  const toggleLibrary = (indexId: string) => {
    const enabled = enabledIndexIds.includes(indexId)
    if (enabled) {
      onUpdate({ enabledIndexIds: enabledIndexIds.filter((id) => id !== indexId) })
    } else {
      onUpdate({ enabledIndexIds: [...enabledIndexIds, indexId] })
    }
  }

  const toggleAllLibraries = (checked: boolean) => {
    if (checked) {
      onUpdate({ enabledIndexIds: availableLibraries.map((lib) => lib.id) })
    } else {
      onUpdate({ enabledIndexIds: [] })
    }
  }

  return (
    <div className="space-y-4">
      {availableLibraries.length > 0 && (
        <>
          <div className="flex items-center justify-between rounded-md border p-3">
            <Label className="text-sm" htmlFor="toggle-all-libraries">
              {t("enable_all")}
            </Label>
            <Switch
              checked={isAllLibrariesEnabled}
              id="toggle-all-libraries"
              onCheckedChange={toggleAllLibraries}
            />
          </div>
          <Separator />
        </>
      )}

      <div className="space-y-2">
        {availableLibraries.map((library) => (
          <div
            key={library.id}
            className="hover:bg-accent/50 flex items-center justify-between rounded-lg border p-3 transition-colors"
          >
            <div className="grid min-w-0 flex-1 gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Label
                    className="cursor-pointer text-sm font-medium"
                    htmlFor={`library-${library.id}`}
                  >
                    {library.display_name}
                  </Label>
                </TooltipTrigger>
                <TooltipContent className="max-w-[300px]" side="right">
                  <p>{library.description}</p>
                </TooltipContent>
              </Tooltip>
            </div>
            <Switch
              checked={enabledIndexIds.includes(library.id)}
              id={`library-${library.id}`}
              onCheckedChange={() => toggleLibrary(library.id)}
            />
          </div>
        ))}
      </div>

      {availableLibraries.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground text-sm">{t("no_libraries")}</p>
        </div>
      )}
    </div>
  )
}
